import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import {
	addEntityDocumentSignOff,
	removeEntityDocumentSignOff,
	renameDocument
} from '../../../../../../../actions/Document/entityDocumentActions';
import Utility from '../../../../../../../util/utilityfunctions';
import {actionTags, entities, IndividualFitDocumentTypes} from '../../../../../../../util/uiconstants';
import DocumentName from '../../../../../DocumentName/DocumentName';
import NotesCount from '../../../../../ReviewNotes/NotesCount';
import SignOffs from '../../../../../SignOffs/SignOffs';
import {useUserPreferences, useDocumentId} from '../../../../../../../util/customHooks';
import ValidationControl from '../../../../../CanvasForm/Common/ValidationControl';
import {getValidationCountForIds} from '../../../../../CanvasForm/Common/ValidationUtility';
import {getValidationsForDocumentId} from '../../../../../../../selectors/Forms/canvasFormSelectors';
import {labels} from '../../../../../../../util/utils';
import {getTaskDocumentByDocumentId} from '../../../../../../../selectors/Ares/selectors';

const Document = ({
	document,
	entityUId,
	loadDocuments,
	deleteDocumentAction,
	viewKey = 'ACCOUNT_BODY_DOCUMENT',
	taskId = null,
	hasRelateTask,
	accountSTEntityId
}) => {
	const dispatch = useDispatch(),
		documentId = useDocumentId();

	const currentDocument = useSelector((state) => getTaskDocumentByDocumentId(state, documentId));

	const engagementId = useSelector((state) => state.engagementId),
		userPreferences = useUserPreferences();

	const [approvals, setApprovals] = useState(
		formatGetAllDocumentsApprovalsForSignOffsControl(document?.approvals || [])
	);

	const isPriorPeriodEvidence = Utility.isPriorPeriodEvidence(document);
	let showDocumentOptions = true;

	const documentOptions = [
		{name: 'Open'},
		{name: 'Open read-only'},
		{name: 'CopyLink'},
		{name: 'Rename'},
		{name: 'RelateTasks'},
		{name: 'RelateEntities'},
		{name: 'SignoffRequirements'},
		{name: 'Delete'}
	];

	//Hide Open read-only for individual profile documents
	if (IndividualFitDocumentTypes.includes(document.documentTypeId)) {
		documentOptions.filter((option) => option.name !== 'Open read-only');
	}

	const validations = useSelector((store) => getValidationsForDocumentId(store, document.id));

	const {
		unresolvedCount: comments,
		incompleteCount: incomplete,
		guidedWorkFlowValidations: guidedWorkFlowValidations
	} = getValidationCountForIds(undefined, undefined, undefined, validations, undefined, document.documentTypeId, true);

	useEffect(() => {
		if (document) {
			if (document.approvals) {
				setApprovals(Utility.formatGetAllDocumentsApprovalsForSignOffsControl(document.approvals, userPreferences));
			}
		}
	}, [document]);

	function formatGetAllDocumentsApprovalsForSignOffsControl(approvals) {
		return approvals.map((approval) => {
			return {
				approvalId: approval.id,
				name: approval.approvalUserName,
				userInitials: approval.approvalUserInititals ? approval.approvalUserInititals : approval.userInitials,
				isPreparer: approval.isPreparer,
				isFromLoggedInUser: userPreferences.id === approval.approvalUserId ? true : false,
				createDate: approval.createDate
			};
		});
	}

	const handleOnDocumentRenamed = (document, newDocumentName) => {
		dispatch(renameDocument(document.id, newDocumentName));
	};

	const onDocumentDelete = () => {
		loadDocuments && loadDocuments();
	};

	const DisplayValidations = (document) => {
		if (currentDocument.approvals.length > 0) {
			return true;
		}

		if (currentDocument.approvals.length === 0 && document.approvals.length > 0) {
			return true;
		}

		return false;
	};

	return (
		<DocumentWrapper className="relatedDocumentsItemWrapperContainer">
			<section key={document.id} className={`relatedDocuments__row`}>
				<section className="relatedDocumentDocName">
					<DocumentName
						actionTag={isPriorPeriodEvidence ? actionTags.openReadOnly : actionTags.open}
						engagementId={document.engagementId}
						documentId={document.id}
						documentName={document.documentName}
						documentTasks={document.tasks}
						documentTypeId={document.documentTypeId}
						documentChangeTypeId={document.documentChangeTypeId}
						documentEditUserName={document.documentEditUserName}
						documentEditUserCreateDate={document.documentEditUserCreateDate}
						entities={document.entities}
						entityUId={entityUId}
						confidentialityTypeId={document.confidentialityTypeId}
						enableOpenDocument
						fileExt={document.fileExt}
						fileSizeKB={document.fileSizeKB}
						hasComments={document.hasComments}
						hasHiddenSheets={document.hasHiddenSheets}
						hasHighlights={document.hasHighlights}
						hasAnySystemAssociated={document.hasAnySystemAssociated}
						isReadOnly={document.isReadOnly}
						isConflicted={document.isConflicted}
						isDragAndDropDoc={document.isDragAndDrop}
						isShared={document.isShared}
						isReturned={document.isReturned}
						isEPSignoffRequired={document.isEPSignoffRequired}
						isEPSignoffUserRequired={document.isEPSignoffUserRequired}
						isEQRSignoffRequired={document.isEQRSignoffRequired}
						isEQRSignoffUserRequired={document.isEQRSignoffUserRequired}
						lastModifiedFullName={document.lastModifiedFullName + ' '}
						menuOptions={documentOptions}
						modifiedDate={document.modifiedDate}
						onDocumentRenamed={handleOnDocumentRenamed}
						onDocumentDeleted={onDocumentDelete}
						showCurrentUserInfo
						showConfidential
						showMenuOptions
						showLastModifiedUserInfo
						showShared
						size="small"
						smartEvidenceSourceId={document.smartEvidenceSourceId}
						smartEvidenceSourceEntityId={document.smartEvidenceSourceEntityId}
						trackChange={document.trackChange}
						viewKey={viewKey}
						deleteDocumentAction={deleteDocumentAction}
						hasRelateTask={hasRelateTask}
						accountSTEntityId={accountSTEntityId}
					/>
				</section>
				{showDocumentOptions && (
					<section className="body-quick-reference_row_review-notes">
						<NotesCount
							engagementId={engagementId}
							viewKey={'FORM_ANALYTICS_BODY'}
							entityUId={entityUId}
							document={document}
							isDisabled={false}
						/>
					</section>
				)}
				{showDocumentOptions && (
					<section className="body-quick-reference__row__sign-offs">
						<SignOffs
							documentSignOffs={Array.isArray(approvals) ? approvals : []}
							userDateFormat={userPreferences.dateFormat}
							engagementId={engagementId}
							documentId={document.id}
							document={document}
							taskId={taskId}
							docTypeId={document.documentTypeId}
							onSignOffAdded={(approval) => {
								dispatch(addEntityDocumentSignOff(approval.documentId, approval));
								setApprovals(approvals.concat(approval));
							}}
							onSignOffRemoved={(removedApproval) => {
								dispatch(removeEntityDocumentSignOff(removedApproval.documentId, removedApproval.approvalId));
								setApprovals(
									approvals.filter((approval) => {
										return removedApproval.approvalId !== approval.approvalId;
									})
								);
							}}
						/>
					</section>
				)}
				<span className="section-validationCount">
					{showDocumentOptions && DisplayValidations(document) && (
						<ValidationControl
							documentId={document.id}
							incompleteResponseCount={incomplete ?? 0}
							unresolvedCommentCount={comments ?? 0}
							entityUId={document.id}
							entityId={entities.Document}
							guidedWorkFlowValidationDetails={guidedWorkFlowValidations || []}
							isReadOnly
						/>
					)}
				</span>
			</section>
		</DocumentWrapper>
	);
};

export default Document;

const DocumentWrapper = styled.section`
	width: 100%;
	height: 100%;
	.relatedDocuments__row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: var(--px-10) 0 0 0;
		position: relative;
		height: 100%;
		.relatedDocumentDocName {
			display: inline-flex;
			width: calc(100% - var(--px-390));
			height: 100%;
			.DocumentNameContainer {
				.DocumentNameDetails {
					width: 100%;
					.DocumentName {
						display: inline-block;
						width: 100%;
						.last-modified,
						.currently-modified,
						.coedit-mode-complete {
							margin-top: 0;
						}
						.coedit-mode-complete {
							display: flex;
							.coedit-mode-complete-label {
								margin-right: var(--px-3);
								max-width: 90%;
								display: inline-block;
							}
						}
						.DocumentName {
							color: var(--blue-600) !important;
							font-size: var(--px-14);
							font-weight: 700;
							width: 100%;
							display: inline-block;
							cursor: pointer;
						}
					}
					.input-container {
						input {
							border-radius: var(--px-6);
						}
					}
				}
				&.name-link {
					&:hover,
					&:focus {
						text-decoration: none !important;
					}
				}
			}
			.cogmenu-btn {
				display: flex;
				svg {
					fill: var(--neutrals-700) !important;
					color: var(--neutrals-700) !important;
					width: var(--px-20);
					height: var(--px-20);
				}
				.show-pop-content {
					&.bottom {
						top: var(--px-30);
					}
					&.top {
						bottom: var(--px-45);
					}
				}
			}
			.motif-dropdown-portal {
				position: absolute;
				right: 0;
				margin-top: var(--px-2_5);
				.motif-dropdown-trigger .motif-button .motif-icon svg {
					transform: rotate(180deg);
				}
			}
		}
		.small {
			padding: 0;
			width: 100%;
			svg {
				width: var(--px-26);
				height: var(--px-26);
				max-width: var(--px-26);
				max-height: var(--px-26);
			}
		}
		section:not([class]) {
			color: var(--neutrals-700);
			padding-left: 0;
			.modifiedName {
				margin-right: var(--px-10);
			}
		}
		.DocumentNameWrapper {
			.DocumentNameDetails {
				display: inline-flex;
				.DocumentNameText {
					.DocumentName {
						height: auto;
					}
				}
				.documentName-icons {
					color: var(--neutrals-900);
					svg {
						color: var(--neutrals-900);
						fill: var(--neutrals-900);
						path {
							fill: var(--neutrals-900);
						}
					}
					.document-checked-out-icon {
						margin-right: var(--px-10);
					}
				}
				&.small {
					.DocumentNameDetails {
						.documentName-icons {
							color: var(--neutrals-900);
							svg {
								color: var(--neutrals-900);
								fill: var(--neutrals-900);
								path {
									fill: var(--neutrals-900);
								}
							}
							.motif-tooltip-trigger-wrap {
								.document-confidential {
									svg {
										width: var(--px-16);
										height: var(--px-16);
										max-width: var(--px-16);
										max-height: var(--px-16);
										fill: var(--neutrals-900);
										path {
											fill: var(--neutrals-900);
										}
									}
								}
							}
						}
					}
				}
			}
			&.disable {
				user-select: none;
				pointer-events: none;
				.DocumentNameWrapper {
					color: var(--neutrals-500);
				}
			}
		}
		.relatedDocumentsWrapper {
			display: flex;
		}
		.body-quick-reference_row_review-notes {
			border-right: var(--px-1) solid var(--neutrals-300);
			height: var(--px-60);
			padding: var(--px-10) var(--px-10);
			width: var(--px-70);
			display: inline-flex;
			justify-content: center;
			align-items: center;
			.notesControl {
				padding: 0;
				.btn-default {
					padding: 0;
					margin: 0;
					span {
						color: var(--neutrals-900);
						svg {
							fill: var(--neutrals-900);
						}
					}
					&.simple-button {
						height: var(--px-18);
						width: var(--px-18);
						border: var(--px-1) solid var(--neutrals-900);
						border-radius: var(--px-50);
						color: var(--neutrals-900);
						font-size: var(--px-12);
						span {
							padding: 0;
							width: 100%;
							height: 100%;
							display: flex;
							align-items: center;
							justify-content: center;
						}
						&:hover {
							background-color: var(--neutrals-900);
							span {
								color: var(--neutrals-00white);
								svg {
									fill: var(--neutrals-00white);
								}
							}
						}
						&.removeBorder {
							border: 0;
							&:hover {
								background-color: transparent;
								span {
									color: var(--neutrals-900);
									svg {
										fill: var(--neutrals-900);
									}
								}
							}
						}
					}
				}
			}
		}
	}
	.body-quick-reference__row__sign-offs {
		display: flex;
		justify-content: flex-end;
		padding-right: var(--px-10);
		padding-left: var(--px-10);
		height: var(--px-38);
		width: var(--px-200);
		.evidenceSignOff {
			padding: var(--px-10) 0 0 0;
		}
	}
	.generaterisk-info {
		font-size: var(--px-14);
		font-weight: 300;
		border: var(--px-1) solid var(--neutrals-200);
		background: var(--neutrals-100);
		padding: var(--px-12);
		margin: 0 var(--px-25) var(--px-10) var(--px-25);
		text-align: center;
		.motif-text-link.motif-text-link-alt {
			font-size: var(--px-14);
			font-weight: bold;
			color: var(--blue-600);
			text-decoration: none;
		}
	}
	.createLimitedRiskAccountDocumentationWrapper {
		align-items: center;
		display: inline-flex;
		min-height: var(--px-38);
		font-weight: 700;
		font-size: var(--px-14);
		line-height: var(--px-22);
		color: var(--blue-600);
		.createLimitedRiskAccountDocumentationLabel {
			margin-left: var(--px-4);
			display: inline-flex;
			align-items: center;
			cursor: pointer;
		}
		.createLimitedRiskAccountDocumentationIcon {
			display: inline-flex;
			align-items: center;
			vertical-align: text-bottom;
			cursor: pointer;
			svg {
				width: var(--px-16) !important;
				height: var(--px-16) !important;
				fill: var(--blue-600);
			}
		}
	}
`;
